import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStyles } from './ChatPage.styles';
import ChatMessage from './ChatMessage';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { appActions } from '../../actions/app.actions.js';
import socket from '../../services/socket';
import DeleteModal from '../layout/DeleteModal';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import {
  deleteThread,
  fetchThreads,
  createThreads,
  getThreadHistory,
  getTTSInference,
} from '../../services/page.services';
import phoneCallImg from '../../img/phoneCall.svg';
import sendButtonImg from '../../img/sendButton.svg';

const ChatPage = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  const navigate = useNavigate();
  const { modelName, image, assistantId } = location.state;
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [threadId, setThreadId] = useState(null);
  const [chunkMessage, setChunkMessage] = useState('');
  const [isSendBlocked, setIsSendBlocked] = useState(false);
  const [ttsTrainingId, setTtsTrainingId] = useState(null);
  const [isInputReset, setIsInputReset] = useState(false);
  const [resetKey, setResetKey] = useState('normal'); // 기본 키 값 설정
  const chatContainerRef = useRef(null);
  const user = useSelector(state => state.user);
  const userId = user?.id;
  const [lastId, setLastId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevScrollHeightRef = useRef(0);
  const [isDeleting, setIsDeleting] = useState(false); // 상태 추가
  const isDeletingRef = useRef(false); // 즉각적인 상태 확인용
  const [isCreatingThread, setIsCreatingThread] = useState(false); // UI 렌더링 상태
  const isCreatingThreadRef = useRef(false); // 즉각적인 상태 확인용
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isLocationChanged, setIsLocationChanged] = useState(false);
  const locationChangeRef = useRef(false); // location 변경 여부를 관리하는 Ref
  //TTS가 재생중인지 아닌지(false면 재생중이 아님)
  const [isPlaying, setIsPlaying] = useState(false);
  //socket에서 end이벤트가 왔는지, 나중에 scroll바를 맨 밑으로 내리기 위함
  const [isEnd, setIsEnd] = useState(true);
  const [isVoiceEnd, setIsVoiceEnd] = useState(false);
  const [isInputAvailable, setIsInputAvailable] = useState(true);
  // 녹음 효과 bar 애니메이션 활성화
  const [isRecording, setIsRecording] = useState(false);
  const [currentPlayingMessageId, setCurrentPlayingMessageId] = useState(null);
  const [isVoiceCall, setIsVoiceCall] = useState(false);
  const [isVoiceBlocked, setIsVoiceBlocked] = useState(false);
  const [isbackButtonClicked, setIsbackButtonClicked] = useState(false);
  const [isChunkActive, setIsChunkActive] = useState(false);
  const isChunkActiveRef = useRef(false);
  const messageIdsRef = useRef(new Set());
  // 추가: audio 객체를 상태로 관리
  const [audio, setAudio] = useState(null);
  const audioRef = useRef(null);

  const textareaRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        setAudio(null);
        audioRef.current = null;
      }
      
      locationChangeRef.current = true;
      console.log('뒤로가기 됨');

      setIsLocationChanged(true);
      dispatch(appActions.closeMessageModal()); // 모달 닫기
    };
  }, [location]);

  const handleOpenModal = () => {
    setDeleteModalOpen(true);
  };
  const handleCloseModal = () => {
    setDeleteModalOpen(false);
  };
  const scrollToBottom = () => {
    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  };

  useEffect(() => {
    if (assistantId && userId) {
      fetchAllThreads(assistantId, userId, dispatch);
    }
  }, [assistantId, userId]);

  useEffect(() => {
    if (isInputReset) {
      setResetKey(prevKey => (prevKey === 'normal' ? 'reset' : 'normal')); // 키 값을 토글하여 강제 리렌더링
      setIsInputReset(false); // 리렌더링이 완료되면 상태를 false로 되돌림
    }
  }, [isInputReset]);

  // 리렌더링 후 textarea에 focus
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [resetKey]);

  useEffect(() => {
    if (threadId && socket.connected && !isInitialized) {
      initializeSocket(threadId);
    }
  }, [threadId, assistantId, userId, isInitialized]);

  useEffect(() => {
    socket.on('connect', () => {
      if (threadId) {
        initializeSocket(threadId);
      }
    });

    socket.on('chunk', data => {
      try {
        isChunkActiveRef.current = true; // Set to true when chunk starts
        setIsChunkActive(true);
        setChunkMessage(prev => prev + data.message.replace(/【.*?】/g, ''));
        if (chatContainerRef.current) {
          scrollToBottom();
        }
        setIsEnd(true);
      } catch (error) {
        console.error('Error processing chunk message:', error);
        dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
      }
    });

    socket.on('end', data => {
      try {
        isChunkActiveRef.current = false; // Set to true when chunk starts
        setIsChunkActive(false);
        setIsEnd(true);
        setMessages(prevMessages => {
          const newMessage = {
            role: 'assistant',
            content: data.message,
            messageId: data.messageId,
          };
          messageIdsRef.current.add(data.messageId);
          return [...prevMessages, newMessage];
        });
        setChunkMessage('');
        if (chatContainerRef.current) {
          scrollToBottom();
        }
        if (isVoiceCall) {
          console.log('isVoiceCall in socketEnd:', isVoiceCall);
          handleAutoPlayButtonClick({ message: { messageId: data.messageId, content: data.message } });
        } else {
          setIsSendBlocked(false);
          setIsVoiceBlocked(false);
        }
        console.log("isChunkActive: ",isChunkActive);
      } catch (error) {
        console.error('Error processing end message:', error);
        dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
      }
    });

    socket.on('disconnect', () => {
      try {
        dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
      } catch (error) {
        console.error('Error handling disconnect:', error);
        dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
      }
    });

    return () => {
      socket.off('connect');
      socket.off('chunk');
      socket.off('end');
    };
  }, [chunkMessage]);

  useLayoutEffect(() => {
    const handleScroll = async () => {
      try {
        if (chatContainerRef.current.scrollTop === 0 && hasMore) {
          prevScrollHeightRef.current = chatContainerRef.current.scrollHeight;
          await fetchThreadHistory(threadId, lastId);
        }
      } catch (error) {
        console.error('Error handling scroll:', error);
        dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
      }
    };

    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => {
        chatContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, [lastId, hasMore, threadId]);

  useLayoutEffect(() => {
    if (chatContainerRef.current && prevScrollHeightRef.current && !isEnd) {
      const currentScrollHeight = chatContainerRef.current.scrollHeight;
      chatContainerRef.current.scrollTop = currentScrollHeight - prevScrollHeightRef.current;
    }
    if (isEnd) {
      if (chatContainerRef.current) {
        scrollToBottom();
      }
      setIsEnd(false);
    }
  }, [messages]);

  const fetchAllThreads = async (assistantId, userId, dispatch) => {
    try {
      const allThreads = await fetchThreads();

      if (allThreads) {
        const userThread = allThreads.find(thread => thread.assistantId === assistantId);
        if (userThread) {
          setThreadId(userThread.id);
          setTtsTrainingId(userThread.assistant.TtsTrainingId);
          await fetchThreadHistory(userThread.id);
        } else {
          await createThread();
        }
      } else {
        console.log('Threads fetched, but no data returned.');
        dispatch(appActions.openMessageModal(t('chatPageTab.modal.pageLoadFail'), true));
      }
    } catch (error) {
      console.error('Error fetching all threads:', error);
      dispatch(appActions.openMessageModal(t('chatPageTab.modal.pageLoadFail'), true));
    }
  };

  const fetchThreadHistory = async (threadId, lastId) => {
    try {
      const threadHistory = await getThreadHistory(threadId, lastId);
      const responseData = threadHistory.body;
      let newMessages = threadHistory.data.map(message => ({
        messageId: message.id,
        role: message.role,
        content: message.content
          .map(item => item.text.value.replace(/【.*?】/g, ''))
          .join(' '),
      }));
  
      setHasMore(responseData.has_more);
      setLastId(responseData.last_id);
      // If hasMore is false and the first message's role is 'user', remove it
      if (!responseData.has_more && newMessages.length > 0 && newMessages[0].role === 'user') {
        newMessages.shift(); // Remove the first message from the array
      }
      console.log("isChunkActive in fetchThreadHistory",isChunkActiveRef.current);
      // Filter out messages that have already been added
      newMessages = newMessages.filter(
        message => !messageIdsRef.current.has(message.messageId)
      );
      newMessages.forEach(message =>
        messageIdsRef.current.add(message.messageId)
      );
      if (isChunkActiveRef.current) {
        newMessages.pop(); // Remove the last message from the array
      }
  
      setMessages(prevMessages => [...newMessages, ...prevMessages]);
      
  
      // if (!newMessages.length) {
      //   if (!isInitialized) initializeSocket(threadId);
      //   initializeChat(threadId);
      //   dispatch(appActions.openMessageModal(t('chatPageTab.modal.warningMessage.0'), false));
      // }
      if (chatContainerRef.current && !lastId) {
        setTimeout(() => {
          scrollToBottom();
        }, 0);
      }
    } catch (error) {
      console.error('Error fetching thread history:', error);
      dispatch(appActions.openMessageModal(t('chatPageTab.modal.historyLoadFail'), true));
    }
  };

  const createThread = async () => {
    if (isDeletingRef.current || isCreatingThreadRef.current || isLocationChanged || locationChangeRef.current) {
      // 삭제 중이거나 Thread 생성 중이라면 아무 작업도 하지 않음
      return;
    }

    try {
      setIsCreatingThread(true); // UI 상태 업데이트
      isCreatingThreadRef.current = true; // 즉시 상태 반영
      setIsSendBlocked(true);

      if (locationChangeRef.current) {
        console.log('locationChangeRef.current:', locationChangeRef.current);
        return;
      }
      dispatch(appActions.openMessageModal(t('chatPageTab.modal.warningMessage.0'), false));
      const userThread = await createThreads(assistantId, socket.id);
      setThreadId(userThread.id);
      setTtsTrainingId(userThread.assistant.TtsTrainingId);
      if (!isInitialized) initializeSocket(userThread.id);
      initializeChat(userThread.id);
    } catch (error) {
      console.error('Error creating thread:', error);
      dispatch(appActions.openMessageModal(t('chatPageTab.modal.createFail'), true));
    } finally {
      setIsCreatingThread(false); // UI 상태 업데이트
      isCreatingThreadRef.current = false; // 즉시 상태 반영
    }
  };

  const initializeSocket = threadId => {
    try {
      if (threadId) {
        socket.emit('socketInit', { threadId, assistantId, userId });
        setIsInitialized(true);
      }
    } catch (error) {
      console.error('Error initializing socket:', error);
      dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
    }
  };

  const initializeChat = threadId => {
    try {
      socket.emit('chatInit', { threadId, assistantId, userId });
      console.log('Chat initialized');
    } catch (error) {
      console.error('Error initializing chat:', error);
      dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
    }
  };

  const handleDelete = async () => {
    if (isDeletingRef.current || isCreatingThreadRef.current) {
      // 삭제 중이거나 Thread 생성 중이라면 아무 작업도 하지 않음
      return;
    }
    try {
      if (threadId === null) {
        console.error('Thread ID is null');
        dispatch(appActions.openMessageModal(t('chatPageTab.modal.deleteFail'), true));
        return;
      }
      setIsSendBlocked(true);
      setIsDeleting(true); // UI 상태 업데이트
      isDeletingRef.current = true; // 즉시 상태 반영
      await deleteThread(threadId);
      window.location.reload();
    } catch (error) {
      console.error('Error deleting thread:', error);
      dispatch(appActions.openMessageModal(t('chatPageTab.modal.deleteFail'), true));
    } finally {
      setIsDeleting(false); // UI 상태 업데이트
      isDeletingRef.current = false; // 즉시 상태 반영
      setDeleteModalOpen(false);
    }
  };

  const handleSendMessage = async () => {
    if (input.trim() === '') return;
    if (!threadId) return;
    if (isPlaying) return;
    if (isRecording) return;

    if (!isVoiceCall) {
      if (isSendBlocked) return;
    }
    setIsEnd(false);
    setIsSendBlocked(true);
    setIsVoiceBlocked(true);

    const messageId = 'user_' + Date.now();
    const newMessage = { role: 'user', content: input, messageId };
    setMessages(prevMessages => {
      messageIdsRef.current.add(messageId);
      return [...prevMessages, newMessage];
    });
    setInput('');
    setIsInputReset(true);
    if (chatContainerRef.current) {
      scrollToBottom();
    }

    try {
      socket.emit('chat', { message: input, assistantId, threadId, userId });
      setTimeout(() => {
        if (chatContainerRef.current) {
          scrollToBottom();
        }
      }, 0);
    } catch (error) {
      console.error('Error sending message:', error);
      setIsSendBlocked(false);
      setIsVoiceBlocked(false);
      setMessages('');
      dispatch(appActions.openMessageModal(t('modal.pageLoadFail'), true));
    }
  };

  function autoResizeTextarea(textarea) {
    textarea.style.height = '1rem'; // 먼저 높이를 초기화합니다.
    textarea.style.height = `${textarea.scrollHeight}px`; // 내용에 맞게 높이를 조정합니다.

    // 최대 높이를 초과할 경우 스크롤바를 표시하도록 설정합니다.
    if (textarea.scrollHeight > 147) {
      // 216px은 9줄의 높이입니다.
      textarea.style.overflowY = 'auto';
      textarea.style.height = '147px'; // 최대 높이를 고정합니다.
    } else {
      textarea.style.overflowY = 'hidden'; // 스크롤바를 숨깁니다.
    }
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && event.shiftKey) {
      // Shift+Enter인 경우 줄바꿈을 허용하고, 메시지 전송을 막습니다.
      return; // 아무 작업도 하지 않고 기본 동작(줄바꿈)만 수행
    } else if (event.key === 'Enter') {
      event.preventDefault(); // 기본 Enter 동작 방지
      handleSendMessage(); // Enter를 눌렀을 때 메시지 전송 함수 호출
    }
  };
  const handleStopButtonClick = () => {
    if (!isPlaying) return;
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setAudio(null);
      setIsPlaying(false);
      setIsVoiceBlocked(false);
      setIsSendBlocked(false);
      setIsVoiceCall(false);
      setIsInputAvailable(true);
    }
  };

  const handlePlayButtonClick = async message => {
    if (isRecording || isPlaying) return;
    if (isSendBlocked) return;
    setIsPlaying(true);
    setIsSendBlocked(true);
    setIsVoiceBlocked(true);

    try {
      if (message.message.messageId) {
        setCurrentPlayingMessageId(message.message.messageId);
        const response = await getTTSInference(
          message.message.messageId,
          message.message.content,
          ttsTrainingId,
          50,
          70,
          'PRO',
          socket.id
        );
        playAudioStream(response);
      } else {
        setIsPlaying(false);
        setCurrentPlayingMessageId(null);
        dispatch(appActions.openMessageModal('TTS playback failed', true));
      }
    } catch (error) {
      console.error('Error handling play button click:', error);
      setCurrentPlayingMessageId(null);
      dispatch(appActions.openMessageModal('TTS playback failed', true));
      setIsPlaying(false);
    }
  };
  const handleBackButtonClick = () => {
    if (audio) {
      audio.pause();
      setAudio(null);
    }
    navigate(-1);
    setIsbackButtonClicked(true);
  };

  const handleAutoPlayButtonClick = async message => {
    if (isRecording || isPlaying) return;
    if (isSendBlocked && !isVoiceCall) return;
    setIsPlaying(true);
    setIsSendBlocked(true);
    setIsVoiceBlocked(true);
    try {
      if (message.message.messageId) {
        setCurrentPlayingMessageId(message.message.messageId);
        const response = await getTTSInference(
          message.message.messageId,
          message.message.content,
          ttsTrainingId,
          50,
          70,
          'PRO',
          socket.id
        );
        playAudioStream(response);
      } else {
        setIsPlaying(false);
        setCurrentPlayingMessageId(null);
        dispatch(appActions.openMessageModal('TTS playback failed', true));
      }
    } catch (error) {
      console.error('Error handling play button click:', error);
      setCurrentPlayingMessageId(null);
      dispatch(appActions.openMessageModal('TTS playback failed', true));
      setIsPlaying(false);
    }
  };

  const playAudioStream = async response => {
    const audioElement = new Audio();
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioContext.createMediaElementSource(audioElement);
    source.connect(audioContext.destination);
    const mediaSource = new MediaSource();

    audioElement.src = URL.createObjectURL(mediaSource);
    setAudio(audioElement); // audio 상태 업데이트
    audioRef.current = audioElement;

    mediaSource.addEventListener('sourceopen', async () => {
      const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
      audioElement.play();
      const reader = response.body.getReader();
      let played = false;

      sourceBuffer.addEventListener('updateend', () => {
        if (!played) {
          const bufferdTime = audioElement.buffered.length > 0 ? audioElement.buffered.end(0) : 0;
          if (bufferdTime > 0.3) {
            audioElement.play();
            played = true;
          }
        }
      });

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          mediaSource.endOfStream();
          if (!played) {
            audioElement.play();
            played = true;
          }
          break;
        }
        sourceBuffer.appendBuffer(value);
        await new Promise(resolve => {
          if (sourceBuffer.updating) {
            sourceBuffer.addEventListener('updateend', resolve, { once: true });
          } else {
            resolve();
          }
        });
      }
    });

    audioElement.addEventListener('ended', () => {
      setIsPlaying(false);
      setIsVoiceBlocked(false);
      if (!isVoiceCall) setIsSendBlocked(false);
      // STT 자동 재시작
      if (isVoiceCall) {
        startSTTRecording();
      }
    });
  };
  // STT 녹음 시작 함수
  const startSTTRecording = () => {
    if (!listening) {
      console.log('startSTTRecording');
      setIsVoiceEnd(true);
    }
  };

  // STT
  const useSpeechToText = () => {
    const { transcript, listening, resetTranscript } = useSpeechRecognition();
    const toggleListening = () => {
      if (listening) {
        SpeechRecognition.stopListening();
      } else {
        let language = '';
        switch (currentLanguage) {
          case 'en':
            language = 'en-US';
            break;
          case 'ko':
            language = 'ko-KR';
            break;
          case 'th':
            language = 'th-TH';
            break;
          case 'ja':
            language = 'ja-JP';
            break;
          default:
            language = 'ko-KR';
        }
        SpeechRecognition.startListening({ language: language, continuous: false });
        resetTranscript();
      }
    };
    return { transcript, listening, toggleListening };
  };
  const { transcript, toggleListening, listening } = useSpeechToText();

  useEffect(() => {
    if (isVoiceEnd && isVoiceCall) {
      handleSTTButtonClick();
      setIsVoiceEnd(false);
    }
  }, [isVoiceEnd, isVoiceCall]);

  useEffect(() => {
    // voiceCall 모드에서, recording이 끝난 경우 send message
    if (!isRecording && input && isVoiceCall) {
      handleSendMessage();
    }
  }, [input, isRecording, isVoiceCall]);

  useEffect(() => {
    if (isRecording && !listening) {
      setInput(transcript);
      setIsRecording(false);
      setIsSendBlocked(false);
    }
  }, [listening]);

  const handleSTTButtonClick = () => {
    if (isVoiceBlocked) return;
    if (isPlaying) return;
    if (isRecording) {
      setIsRecording(false);
      setIsVoiceCall(false);
      setIsInputAvailable(true);
      setIsSendBlocked(false);
      toggleListening();
    } else {
      setIsVoiceCall(true);
      setIsRecording(true);
      setIsSendBlocked(true);
      setIsInputAvailable(false);
      setInput('');
      toggleListening();
    }
  };

  return (
    <div className={classes.main}>
      <div className={classes.container}>
        <div className={classes.headerContainer}>
          <div className={classes.headerRow}>
            <button
              className={`${classes.backButton}`}
              onClick={handleBackButtonClick}
            >
              {t('chatPageTab.back')}
            </button>
            <div className={classes.headerButtons}>
              <button
                className={`${classes.deleteButton} ${isSendBlocked ? classes.disabledButton : ''}`}
                disabled={!isInitialized || isDeleting || isCreatingThread || isSendBlocked}
                onClick={handleOpenModal}
              >
                {t('chatPageTab.delete')}
              </button>
              <DeleteModal
                open={isDeleteModalOpen && !isDeleting && !isCreatingThread && !isSendBlocked}
                onClose={handleCloseModal}
                onDelete={handleDelete}
              />
            </div>
          </div>
        </div>
        <div className={classes.profileContainer}>
          {image && <img src={image} alt={modelName} className={classes.image} />}
          <h3 className={classes.header}>{modelName}</h3>
          <h6 className={classes.warning}>{t('chatPageTab.modal.warningMessage.1')}</h6>
        </div>
        <div className={classes.chatContainer} ref={chatContainerRef}>
          {messages.map((message, index) => (
            <ChatMessage
              key={index}
              message={message}
              image={image}
              modelName={modelName}
              handlePlayButtonClick={() => handlePlayButtonClick({ message })}
              handleStopButtonClick={handleStopButtonClick}
              isPlaying={isPlaying}
              messageId={message.messageId}
              isAnimating={currentPlayingMessageId === message.messageId}
              isSendAvailable={isSendBlocked}
            />
          ))}
          {chunkMessage && (
            <ChatMessage
              key="chunk"
              message={{ role: 'assistant', content: chunkMessage }}
              image={image}
              modelName={modelName}
              handlePlayButtonClick={handlePlayButtonClick}
              isPlaying={isPlaying}
            />
          )}
        </div>

        <div className={classes.inputContainer}>
          <div className={classes.sendContainer}>
            <textarea
              ref={textareaRef}
              key={resetKey}
              type="text"
              className={classes.input}
              value={isRecording ? transcript : input}
              placeholder={t('chatPageTab.send')}
              onChange={e => {
                setInput(e.target.value);
                setIsInputReset(false); // 사용자가 입력을 시작하면 리셋 상태를 해제
              }}
              readOnly={!isInputAvailable}
              onInput={e => autoResizeTextarea(e.target)}
              onKeyDown={handleKeyDown}
            />
            <button
              className={`${classes.sendButton} ${isSendBlocked ? 'disabled' : ''} ${isPlaying ? 'disabled' : ''}`}
              onClick={handleSendMessage}
              disabled={isSendBlocked}
            >
              <img src={sendButtonImg} alt="Send" className={classes.sendIcon} />
            </button>
            {isRecording && (
              <div className={classes.bars}>
                <div className={classes.bar}></div>
                <div className={classes.bar}></div>
                <div className={classes.bar}></div>
              </div>
            )}
          </div>
          <button
            className={`${classes.sttButton} ${isRecording ? 'recording' : ''} ${isPlaying ? 'disabled' : ''} ${isVoiceBlocked ? 'disabled' : ''}`}
            onClick={handleSTTButtonClick}
          >
            <img src={phoneCallImg} alt="STT" className={classes.voiceIcon} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
